// Cow API endpoints using RTK Query

import { baseApi } from './baseApi'
import { API_ENDPOINTS } from '@/constants'
import type {
  Cow,
  CreateCowData,
  UpdateCowData,
  CowFilters,
  CowStatistics,
  HealthRecord,
  WeightRecord,
  PaginatedResponse,
} from '@/types'

export const cowApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all cows with filters
    getCows: builder.query<PaginatedResponse<Cow>, CowFilters | void>({
      query: (filters) => ({
        url: API_ENDPOINTS.COWS.BASE,
        params: filters || {},
      }),
      // Backend returns PaginatedResponse directly (not wrapped in ApiResponse)
      transformResponse: (response: PaginatedResponse<Cow>) => response,
      providesTags: (result) =>
        result && result.data && Array.isArray(result.data)
          ? [
              ...result.data.map(({ id }) => ({ type: 'Cow' as const, id })),
              { type: 'Cow', id: 'LIST' },
            ]
          : [{ type: 'Cow', id: 'LIST' }],
    }),

    // Get cow by ID
    getCowById: builder.query<Cow, string>({
      query: (id) => API_ENDPOINTS.COWS.DETAIL(id),
      transformResponse: (response: Cow) => response,
      providesTags: (_result, _error, id) => [{ type: 'Cow', id }],
    }),

    // Get cows by farm ID
    getCowsByFarm: builder.query<PaginatedResponse<Cow>, string>({
      query: (farmId) => API_ENDPOINTS.FARMS.COWS(farmId),
      transformResponse: (response: PaginatedResponse<Cow>) => response,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'Cow' as const, id })),
              { type: 'Cow', id: 'LIST' },
            ]
          : [{ type: 'Cow', id: 'LIST' }],
    }),

    // Create cow
    createCow: builder.mutation<Cow, CreateCowData>({
      query: (data) => ({
        url: API_ENDPOINTS.COWS.CREATE,
        method: 'POST',
        body: data,
      }),
      transformResponse: (response: Cow) => response,
      invalidatesTags: [{ type: 'Cow', id: 'LIST' }, { type: 'Farm', id: 'LIST' }],
    }),

    // Update cow
    updateCow: builder.mutation<Cow, { id: string; data: UpdateCowData }>({
      query: ({ id, data }) => ({
        url: API_ENDPOINTS.COWS.UPDATE(id),
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: Cow) => response,
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Cow', id },
        { type: 'Cow', id: 'LIST' },
        { type: 'Farm', id: 'LIST' },
      ],
    }),

    // Delete cow
    deleteCow: builder.mutation<void, string>({
      query: (id) => ({
        url: API_ENDPOINTS.COWS.DELETE(id),
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Cow', id },
        { type: 'Cow', id: 'LIST' },
        { type: 'Farm', id: 'LIST' },
      ],
    }),

    // Get cow statistics
    getCowStatistics: builder.query<CowStatistics, void>({
      query: () => API_ENDPOINTS.COWS.STATISTICS,
      transformResponse: (response: CowStatistics) => response,
      providesTags: ['Cow'],
    }),

    // Get health records for a cow
    getHealthRecords: builder.query<HealthRecord[], string>({
      query: (cowId) => API_ENDPOINTS.COWS.HEALTH_RECORDS(cowId),
      transformResponse: (response: HealthRecord[]) => response,
      providesTags: (_result, _error, cowId) => [{ type: 'Cow', id: cowId }],
    }),

    // Get weight records for a cow
    getWeightRecords: builder.query<WeightRecord[], string>({
      query: (cowId) => API_ENDPOINTS.COWS.WEIGHT_RECORDS(cowId),
      transformResponse: (response: WeightRecord[]) => response,
      providesTags: (_result, _error, cowId) => [{ type: 'Cow', id: cowId }],
    }),
  }),
})

export const {
  useGetCowsQuery,
  useGetCowByIdQuery,
  useGetCowsByFarmQuery,
  useCreateCowMutation,
  useUpdateCowMutation,
  useDeleteCowMutation,
  useGetCowStatisticsQuery,
  useGetHealthRecordsQuery,
  useGetWeightRecordsQuery,
} = cowApi
